import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';

const Clienti = ({ clienti, setClienti, currentUser, currentUserRole }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientName, setClientName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);

  // Role helpers
  const isOperator = (currentUserRole === 'Operatori' || currentUserRole === 'Operator');
  const isAdmin = (currentUserRole === 'Admin' || currentUserRole === 'Administrator');

  // Filter states
  const [filters, setFilters] = useState({
    nume: ''
  });

  // Sort states
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'default'
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [inputValue, setInputValue] = useState('25');

  useEffect(() => {
    fetchClienti();
    const onRefresh = () => fetchClienti();
    window.addEventListener('refresh-clienti', onRefresh);
    return () => window.removeEventListener('refresh-clienti', onRefresh);
  }, []);

  const fetchClienti = async () => {
    try {
  const response = await axios.get(apiUrl('/clienti'));
      setClienti(response.data);
    } catch (error) {
      console.error('Error fetching clienti:', error);
      setError('Eroare la încărcarea clienților');
    }
  };

  // CSV Import/Export Functions
  const handleCSVImport = (event) => {
    if (isOperator) {
      setError('Operatorii nu pot importa clienți.');
      event.target.value = '';
      return;
    }
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    setError('');

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        const lines = content.split('\n').filter(line => line.trim());
        
        if (lines.length === 0) {
          throw new Error('Fișierul CSV este gol');
        }

        // Parse header
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        
        if (!headers.includes('nume')) {
          throw new Error('Fișierul CSV trebuie să conțină coloana "nume"');
        }

        // Parse data rows
        const newClienti = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',');
          if (values.length > 0 && values[0].trim()) {
            const client = {
              nume: values[0].trim(),
              createdBy: currentUser
            };
            newClienti.push(client);
          }
        }

        if (newClienti.length === 0) {
          throw new Error('Nu s-au găsit date valide în fișier');
        }

        // Add clients to backend
        Promise.all(
          newClienti.map(client => 
            axios.post(apiUrl('/clienti'), client)
          )
        ).then(() => {
          fetchClienti();
          setError(`${newClienti.length} clienți importați cu succes!`);
          setTimeout(() => setError(''), 3000);
        }).catch(error => {
          console.error('Error importing clients:', error);
          setError('Eroare la importarea clienților');
        }).finally(() => {
          setImporting(false);
        });

      } catch (error) {
        console.error('Error parsing CSV:', error);
        setError(error.message || 'Eroare la procesarea fișierului CSV');
        setImporting(false);
      }
    };

    reader.readAsText(file);
    event.target.value = '';
  };

  const exportToCSV = () => {
    if (isOperator) {
      setError('Operatorii nu pot exporta clienți.');
      return;
    }
    const filteredData = getFilteredAndSortedClienti();
    
    if (filteredData.length === 0) {
      setError('Nu există date pentru export');
      return;
    }

    const headers = ['nume', 'createdBy'];
    const csvContent = [
      headers.join(','),
      ...filteredData.map(client => 
        headers.map(header => client[header] || '').join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `clienti_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setError('Export realizat cu succes!');
    }
    
    setTimeout(() => setError(''), 3000);
  };

  const triggerFileInput = () => {
    if (isOperator) {
      setError('Operatorii nu pot importa clienți.');
      return;
    }
    document.getElementById('csv-file-input-clienti').click();
  };

  // Filter and sort functions
  const getFilteredAndSortedClienti = () => {
    let filtered = clienti.filter(client => {
      return client.nume.toLowerCase().includes(filters.nume.toLowerCase());
    });

    if (sortConfig.key && sortConfig.direction !== 'default') {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  };

  // Pagination functions
  const getTotalPages = () => {
    return Math.ceil(getFilteredAndSortedClienti().length / itemsPerPage);
  };

  const getPaginatedClienti = () => {
    const filtered = getFilteredAndSortedClienti();
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filtered.slice(startIndex, startIndex + itemsPerPage);
  };

  // Event handlers
  const handleAddClient = () => {
    if (isOperator) {
      setError('Operatorii nu pot adăuga clienți.');
      return;
    }
    setEditingClient(null);
    setClientName('');
    setShowModal(true);
    setError('');
  };

  const handleEditClient = (client) => {
    if (isOperator) {
      setError('Operatorii nu pot edita clienți.');
      return;
    }
    setEditingClient(client);
    setClientName(client.nume);
    setShowModal(true);
    setError('');
  };

  const handleDeleteClient = async (client) => {
    if (isOperator || !canUserEdit(client)) {
      setError('Nu aveți permisiunea să ștergeți acest client');
      return;
    }

    if (!window.confirm(`Sigur doriți să ștergeți clientul "${client.nume}"?`)) {
      return;
    }

    setLoading(true);
    try {
  await axios.delete(apiUrl(`/clienti/${client.id}`), {
        data: { currentUser: currentUser?.username }
      });
      await fetchClienti();
      setError('Client șters cu succes!');
      setTimeout(() => setError(''), 3000);
    } catch (error) {
      console.error('Error deleting client:', error);
      setError(error.response?.data?.error || 'Eroare la ștergerea clientului');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClient = async () => {
    if (isOperator) {
      setError('Operatorii nu pot salva modificări.');
      return;
    }
    if (!clientName.trim()) {
      setError('Numele clientului este obligatoriu');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (editingClient) {
        // Edit existing client
  await axios.put(apiUrl(`/clienti/${editingClient.id}`), {
          nume: clientName.trim(),
          currentUser: currentUser?.username
        });
      } else {
        // Add new client
  await axios.post(apiUrl('/clienti'), {
          nume: clientName.trim(),
          createdBy: currentUser?.username
        });
      }

      await fetchClienti();
      setShowModal(false);
      setClientName('');
      setEditingClient(null);
      setError(editingClient ? 'Client editat cu succes!' : 'Client adăugat cu succes!');
      setTimeout(() => setError(''), 3000);
    } catch (error) {
      console.error('Error saving client:', error);
      setError(error.response?.data?.error || 'Eroare la salvarea clientului');
    } finally {
      setLoading(false);
    }
  };

  const canUserEdit = () => {
    return !isOperator;
  };

  // Filter handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Sort handlers
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key ? 
        (prev.direction === 'asc' ? 'desc' : prev.direction === 'desc' ? 'default' : 'asc') : 'asc'
    }));
    setCurrentPage(1);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '⇅';
    if (sortConfig.direction === 'asc') return '↑';
    if (sortConfig.direction === 'desc') return '↓';
    return '⇅';
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, getTotalPages())));
  };

  const handleItemsPerPageChange = (value) => {
    const numValue = parseInt(value);
    if (numValue >= 1 && numValue <= 1000) {
      setItemsPerPage(numValue);
      setCurrentPage(1);
    }
    setInputValue(value);
  };

  return (
    <div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>🏭 Clienți</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {!isOperator && (
              <button 
                className="btn btn-primary"
                onClick={handleAddClient}
                disabled={loading}
              >
                ➕ Adaugă Client
              </button>
            )}
            <input
              id="csv-file-input-clienti"
              type="file"
              accept=".csv"
              onChange={handleCSVImport}
              style={{ display: 'none' }}
            />
            {!isOperator && (
              <>
                <button 
                  className="btn btn-secondary"
                  onClick={triggerFileInput}
                  disabled={loading || importing}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  {importing ? '📂 Importă...' : '📂 Importă CSV'}
                </button>
                <button 
                  className="btn btn-success"
                  onClick={exportToCSV}
                  disabled={loading}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  📤 Export CSV
                </button>
              </>
            )}
           </div>
         </div>

        {error && (
          <div style={{ 
            background: error.includes('succes') ? '#d4edda' : '#f8d7da', 
            color: error.includes('succes') ? '#155724' : '#721c24', 
            padding: '0.75rem', 
            borderRadius: '4px', 
            marginBottom: '1rem',
            border: `1px solid ${error.includes('succes') ? '#c3e6cb' : '#f5c6cb'}`
          }}>
            {error}
          </div>
        )}

        {/* Filters */}
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem', 
            marginBottom: '0.5rem',
            flexWrap: 'wrap'
          }}>
            <input
              type="text"
              value={filters.nume}
              onChange={(e) => handleFilterChange('nume', e.target.value)}
              placeholder="Nume"
              style={{ 
                flex: 1,
                padding: '0.5rem', 
                borderRadius: '4px', 
                border: '1px solid #ced4da',
                fontSize: '0.9rem'
              }}
            />
            <button 
              className="btn btn-outline"
              onClick={() => handleSort('nume')}
              style={{ 
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                fontSize: '0.8rem'
              }}
            >
              Nume {getSortIcon('nume')}
            </button>
          </div>

          {filters.nume && (
            <div style={{ 
              background: '#e7f3ff', 
              color: '#0066cc', 
              padding: '0.5rem', 
              borderRadius: '4px', 
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              border: '1px solid #b3d9ff'
            }}>
              Filtru activ: <strong>"{filters.nume}"</strong> - 
              <span style={{ marginLeft: '0.5rem' }}>
                {getFilteredAndSortedClienti().length} din {clienti.length} clienți
              </span>
              <button 
                onClick={() => handleFilterChange('nume', '')}
                style={{ 
                  marginLeft: '0.5rem', 
                  background: 'none', 
                  border: 'none', 
                  color: '#0066cc', 
                  cursor: 'pointer', 
                  textDecoration: 'underline' 
                }}
              >
                Șterge filtru
              </button>
            </div>
          )}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Nume Client</th>
                <th>Creat de</th>
                <th>Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedClienti().map(client => (
                <tr key={client.id}>
                  <td>{client.nume}</td>
                  <td>{client.createdBy}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {!isOperator && (
                      <button
                         className={`btn btn-warning`}
                         onClick={() => handleEditClient(client)}
                         disabled={isOperator || loading}
                         style={{ 
                           fontSize: '0.8rem', 
                           padding: '0.25rem 0.5rem'
                         }}
                       >
                         ✏️ Editează
                      </button>
                      )}
                      {!isOperator && (
                      <button
                         className={`btn btn-danger`}
                         onClick={() => handleDeleteClient(client)}
                         disabled={isOperator || loading}
                         style={{ 
                           fontSize: '0.8rem', 
                           padding: '0.25rem 0.5rem'
                         }}
                       >
                         🗑️ Șterge
                      </button>
                      )}
                     </div>
                   </td>
                 </tr>
               ))}
             </tbody>
           </table>
         </div>

        {getFilteredAndSortedClienti().length === 0 && clienti.length > 0 && (
          <p style={{ textAlign: 'center', color: '#666', margin: '2rem 0' }}>
            Nu s-au găsit clienți care să corespundă filtrelor aplicate.
          </p>
        )}

        {clienti.length === 0 && (
          <p style={{ textAlign: 'center', color: '#666', margin: '2rem 0' }}>
            Nu există clienți adăugați. Folosiți butonul "Adaugă Client" sau funcția "Importă CSV" pentru a adăuga clienți.
          </p>
        )}

        {/* Pagination */}
        {getFilteredAndSortedClienti().length > 0 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginTop: '1rem', 
            padding: '1rem 0',
            borderTop: '1px solid #dee2e6'
          }}>
            {/* Items per page selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.9rem', whiteSpace: 'nowrap' }}>Afișează:</span>
              <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                {[10, 25, 50, 100].map(size => (
                  <button
                    key={size}
                    className={`btn ${itemsPerPage === size ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => handleItemsPerPageChange(size.toString())}
                    style={{ 
                      padding: '0.25rem 0.5rem', 
                      fontSize: '0.8rem',
                      minWidth: '40px'
                    }}
                  >
                    {size}
                  </button>
                ))}
              </div>
              <input
                type="number"
                min="1"
                max="1000"
                value={inputValue}
                onChange={(e) => handleItemsPerPageChange(e.target.value)}
                onBlur={() => {
                  if (!inputValue || parseInt(inputValue) < 1 || parseInt(inputValue) > 1000) {
                    setInputValue(itemsPerPage.toString());
                  }
                }}
                style={{ 
                  width: '60px', 
                  padding: '0.25rem', 
                  borderRadius: '4px', 
                  border: '1px solid #ced4da',
                  fontSize: '0.8rem',
                  textAlign: 'center'
                }}
              />
              <span style={{ fontSize: '0.9rem', whiteSpace: 'nowrap' }}>pe pagină</span>
            </div>

            {/* Page navigation */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                className="btn btn-outline"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
              >
                Prima
              </button>
              <button
                className="btn btn-outline"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
              >
                ← Precedenta
              </button>
              
              <span style={{ 
                fontSize: '0.9rem', 
                padding: '0 0.5rem',
                whiteSpace: 'nowrap'
              }}>
                Pagina {currentPage} din {getTotalPages()}
              </span>
              
              <button
                className="btn btn-outline"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === getTotalPages()}
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
              >
                Următoarea →
              </button>
              <button
                className="btn btn-outline"
                onClick={() => handlePageChange(getTotalPages())}
                disabled={currentPage === getTotalPages()}
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
              >
                Ultima
              </button>
            </div>

            {/* Total count */}
            <div style={{ fontSize: '0.9rem', color: '#666', whiteSpace: 'nowrap' }}>
              Total: {getFilteredAndSortedClienti().length} clienți
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && !isOperator && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '400px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ marginBottom: '1.5rem' }}>
              {editingClient ? '✏️ Editează Client' : '➕ Adaugă Client Nou'}
            </h3>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Nume Client:
              </label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #ced4da',
                  fontSize: '1rem'
                }}
                placeholder="Introduceți numele clientului"
              />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowModal(false);
                  setClientName('');
                  setEditingClient(null);
                  setError('');
                }}
                disabled={loading}
              >
                Anulează
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveClient}
                disabled={loading || !clientName.trim()}
              >
                {loading ? 'Se salvează...' : (editingClient ? 'Actualizează' : 'Adaugă')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clienti;
